import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Loader2, Wrench } from "lucide-react";

const ACTIVE = ["queued", "running", "paused_disk", "staging"];
const gb = (b) => (b ? `${(b / 1024 ** 3).toFixed(2)} GB` : "—");

export default function RebuildAnalyticsPanel({ canStart }) {
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/data-files/rebuild-analytics/latest").then((r) => setJob(r.data?.id ? r.data : null)).catch(() => {});
  const running = job && ACTIVE.includes(job.status);
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [running]);

  const start = async () => {
    if (!window.confirm("Rebuild customer aggregates, daily/monthly/SKU summaries, RFM and dashboard caches? Safe to re-run; dashboards stay usable.")) return;
    setBusy(true);
    try {
      const r = await api.post("/data-files/rebuild-analytics");
      setJob(r.data);
      toast.success("Analytics rebuild started");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start rebuild");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-black/10 p-5 flex flex-wrap items-center gap-4" data-testid="rebuild-analytics-panel">
      <div className="flex-1 min-w-[240px]">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Data Ops · Rebuild analytics</div>
        <div className="text-sm text-neutral-700">Re-runs customer aggregates, summaries, temp-collection cleanup and compaction, then RFM and cache warm-up on this database.</div>
        {job && (
          <div className="mt-2 text-xs text-neutral-600" data-testid="rebuild-analytics-status">
            {running && <Loader2 className="w-3 h-3 animate-spin inline mr-1" />}
            <b className="uppercase">{job.status}</b> · {job.message} · disk free {gb(job.disk_free)}
            {job.total_seconds ? ` · ${Math.round(job.total_seconds)} s` : ""}
            {job.post_refresh_at ? " · RFM & caches refreshed" : job.status === "done" ? " · refreshing RFM & caches…" : ""}
          </div>
        )}
      </div>
      {canStart && (
        <button className="k-btn k-btn-outline k-btn-sm" disabled={busy || running} onClick={start} data-testid="rebuild-analytics-btn">
          <Wrench className="w-3.5 h-3.5" /> {running ? "Rebuilding…" : "Rebuild analytics"}
        </button>
      )}
    </div>
  );
}
